import classNames from 'classnames';
import Link from 'next/link';
import type { FC, MutableRefObject } from 'react';
import { ArrowIcon } from 'src/components/shared/elements/icons/ArrowIcon';
import { ImageList } from 'src/components/shared/templates/footer/AllBrands/ImageList';
import { ToggleButton } from 'src/components/shared/templates/footer/AllBrands/ToggleButton';
import { KaimonoWebRoutes } from 'src/config/routes';
import type { BrandSchema } from 'src/models/brand/schema';

type Props = {
  handleToggleClick: () => void;
  handleClickSubCategory: (id: number) => void;
  initialVisibleCount: number;
  initialEndRef: MutableRefObject<HTMLDivElement | null>;
  listEndRef: MutableRefObject<HTMLDivElement | null>;
  categoryRefs: MutableRefObject<(HTMLLIElement | null)[]>;
  Heading: 'h2' | 'h3';
  headingTitle: string;
  expandedIndex: number | null;
  isSeeMoreButtonExpanded: boolean;
  visibleCount: number;
  footerImageList: {
    path: string;
    width: number;
    height: number;
  }[];
  data: { slug: string; name: string; brands: BrandSchema[] }[];
  hasSubCategories: boolean;
};

export const Presenter: FC<Props> = ({
  handleToggleClick,
  handleClickSubCategory,
  initialVisibleCount,
  initialEndRef,
  listEndRef,
  categoryRefs,
  Heading,
  headingTitle,
  expandedIndex,
  isSeeMoreButtonExpanded,
  visibleCount,
  footerImageList,
  data,
  hasSubCategories
}) => {
  const isToggleButtonVisible = initialVisibleCount < data.length;

  const renderCategoryOnlyBrands = () => (
    <ul className={classNames('text-xs px-7 py-4 grid grid-cols-1 gap-5', 'semiSm:grid-cols-5 semiLg:mb-10')}>
      {data[0].brands.map((brand) => (
        <li key={brand.brandId} className="text-xs">
          <Link href={KaimonoWebRoutes.brands.show(brand.brandId)}>
            <a className="text-darkSlate">{brand.name}</a>
          </Link>
        </li>
      ))}
    </ul>
  );

  const renderCategoryList = () => (
    <ul className={classNames({ 'semiLg:mb-10': !isToggleButtonVisible })}>
      {data.slice(0, visibleCount).map((subCategory, index) => (
        <li key={subCategory.slug} ref={(el) => (categoryRefs.current[index] = el)}>
          <div
            className="px-5 py-2 border-b border-gray100 border-solid flex justify-between items-center cursor-pointer"
            onClick={() => handleClickSubCategory(index)}
          >
            <div className="text-sm">{subCategory.name}</div>
            <div className=" w-5 h-5 flex items-center justify-center">
              <ArrowIcon direction={expandedIndex === index ? 'top' : 'bottom'} />
            </div>
          </div>
          {expandedIndex === index && (
            <ul className={classNames('text-xs px-7 py-4 grid grid-cols-1 gap-5', 'semiSm:grid-cols-5')}>
              {subCategory.brands.map((brand) => (
                <li key={brand.brandId} className="text-xs">
                  <Link href={KaimonoWebRoutes.brands.show(brand.brandId)}>
                    <a className="text-darkSlate">{brand.name}</a>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
      <div ref={listEndRef} />
      {isToggleButtonVisible && (
        <div className={classNames('flex justify-center py-4', 'semiLg:mb-10')}>
          <ToggleButton onClick={handleToggleClick} isExpanded={isSeeMoreButtonExpanded} />
        </div>
      )}
    </ul>
  );
  return (
    <div className="flex flex-col">
      <div ref={initialEndRef} />
      <Heading className="px-5 py-2.5 border-b border-black border-solid" data-testid="large-category">
        {headingTitle}
      </Heading>
      {hasSubCategories ? renderCategoryList() : renderCategoryOnlyBrands()}
      <div className="flex justify-center semiLg:hidden">
        <ImageList footerImageList={footerImageList} />
      </div>
    </div>
  );
};
